/**
 * Number conversion utilities following satoshi scale (1e8 = 100_000_000)
 * Consistent with Go implementation: safem.SatoshiScale
 * 
 * Following morphcore precision standards: 8 decimal places (satoshi)
 * Example: "1.0" -> 100000000, "1.5" -> 150000000
 */

/**
 * Satoshi scale constant: 1e8 (8 decimal places)
 * Following morphcore precision standards
 * Matches: safem.SatoshiScale in Go codebase
 */
export const SATOSHI_SCALE = 100_000_000;

/**
 * Converts a decimal value to satoshi-scaled string
 * Used for API requests where values must be in satoshi format
 * 
 * @param value - Decimal number (e.g., 1.5) or string representation
 * @returns Satoshi-scaled string (e.g., "150000000")
 * @throws Error if value is invalid or negative
 * 
 * @example
 * toSatoshi(1.0) // "100000000"
 * toSatoshi("1.5") // "150000000"
 * toSatoshi(60000) // "6000000000000"
 */
export function toSatoshi(value: number | string): string {
  const num = typeof value === 'string' ? parseFloat(value) : value;
  
  if (isNaN(num)) {
    throw new Error(`Invalid value for satoshi conversion: ${value}`);
  }
  
  if (num < 0) {
    throw new Error(`Negative values not allowed for satoshi conversion: ${value}`);
  }
  
  // Use BigInt for precision with large numbers
  // Multiply by scale and convert to string
  const scaled = BigInt(Math.floor(num * SATOSHI_SCALE));
  return scaled.toString();
}

/**
 * Converts a satoshi-scaled string to decimal number
 * Used for displaying API responses in human-readable format
 * 
 * @param satoshi - Satoshi-scaled string (e.g., "150000000") or number
 * @returns Decimal number (e.g., 1.5)
 * 
 * @example
 * fromSatoshi("100000000") // 1.0
 * fromSatoshi("150000000") // 1.5
 * fromSatoshi("6000000000000") // 60000.0
 */
export function fromSatoshi(satoshi: string | number): number {
  const num = typeof satoshi === 'string' ? BigInt(satoshi) : BigInt(satoshi);
  return Number(num) / SATOSHI_SCALE;
}

/**
 * Converts a decimal value to satoshi-scaled number (uint64 equivalent)
 * Note: JavaScript numbers are safe up to 2^53-1, so use string for very large values
 * 
 * @param value - Decimal number
 * @returns Satoshi-scaled number (may lose precision for very large values)
 * 
 * @example
 * toSatoshiUint64(1.0) // 100000000
 * toSatoshiUint64(1.5) // 150000000
 */
export function toSatoshiUint64(value: number): number {
  if (value < 0) {
    throw new Error(`Negative values not allowed: ${value}`);
  }
  return Math.floor(value * SATOSHI_SCALE);
}

/**
 * Validates if a string is a valid satoshi-scaled value
 * 
 * @param value - String to validate
 * @returns true if valid satoshi format, false otherwise
 * 
 * @example
 * isValidSatoshi("100000000") // true
 * isValidSatoshi("abc") // false
 * isValidSatoshi("-100") // false
 */
export function isValidSatoshi(value: string): boolean {
  try {
    const num = BigInt(value);
    return num >= 0n;
  } catch {
    return false;
  }
}

/**
 * Converts multiple decimal values to satoshi strings
 * Useful for batch operations
 * 
 * @param values - Array of decimal numbers or strings
 * @returns Array of satoshi-scaled strings
 */
export function toSatoshiBatch(values: (number | string)[]): string[] {
  return values.map(toSatoshi);
}

/**
 * Converts multiple satoshi strings to decimal numbers
 * Useful for batch display operations
 * 
 * @param satoshis - Array of satoshi-scaled strings
 * @returns Array of decimal numbers
 */
export function fromSatoshiBatch(satoshis: (string | number)[]): number[] {
  return satoshis.map(fromSatoshi);
}

/**
 * Convert number to hex string with 0x prefix
 * Used for EIP-712 message fields that require hex format (nonce, deadline)
 * 
 * @param value - Number or string representation of number
 * @returns Hex string with 0x prefix (e.g., "0x1", "0xff")
 * 
 * @example
 * int64ToHex(1) // "0x1"
 * int64ToHex(255) // "0xff"
 * int64ToHex("10") // "0xa"
 */
export function int64ToHex(value: number | string): string {
  const num = typeof value === 'string' ? parseInt(value, 10) : value;
  return `0x${num.toString(16)}`;
}
